import React, { useState } from 'react';

import {
  Button,
  Dialog,
  DialogContent,
  Heading,
  Stack,
  Text,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  useToast,
} from '@eluve/components';
import {
  useAppointmentId,
  useAppointmentLocationId,
  useAppointmentPatient,
  useExternalAppointmentId,
  useIsAppointmentReadonly,
  useSignNoteOnEluve,
  useSummary,
} from '@eluve/frontend-appointment-hooks';
import { useTranslation } from '@eluve/i18n';
import { useCaptureEvent } from '@eluve/posthog-react';
import { useTenantIdFromParams } from '@eluve/session-helpers';

export const SignNoteOnEluveButton: React.FC = () => {
  const { t } = useTranslation();
  const signNoteOnEluve = useSignNoteOnEluve();
  const { isEmpty } = useSummary();

  const [confirmationDialogOpen, setConfirmationDialogOpen] =
    useState<boolean>(false);

  const tenantId = useTenantIdFromParams() ?? '';

  const appointmentId = useAppointmentId();
  const appointmentExternalId = useExternalAppointmentId();
  const isAppointmentReadonly = useIsAppointmentReadonly();
  const appointmentLocationId = useAppointmentLocationId();
  const toast = useToast();

  const captureEvent = useCaptureEvent({
    tenantId,
    appointmentId,
    externalAppointmentId: appointmentExternalId,
  });

  const patient = useAppointmentPatient();
  const patientId = patient?.id;

  const showSuccessToast = (text: string) => {
    toast.success(t('appointmentPage.syncToEhr.noteSignedSuccessfully'), {
      description: text,
    });
  };

  const onButtonClick = async () => {
    // Open the dialog with the confirmation content
    setConfirmationDialogOpen(true);
  };

  const signNoteOnEluveOnly = async (): Promise<void> => {
    setConfirmationDialogOpen(false);
    const isSubmitted = await signNoteOnEluve();

    if (isSubmitted) {
      showSuccessToast(t('appointmentPage.signNoteOnEluve.noteSigned'));
      captureEvent('signed_note_on_eluve_only');
    } else {
      captureEvent('failed_to_sign_note_on_eluve_only');
      toast.error(t('appointmentPage.syncToEhr.signFailed'));
    }
  };

  const signNoteDisabledReasonText = (() => {
    if (isAppointmentReadonly) {
      return t('appointmentPage.signNoteOnEluve.notOwner');
    }
    if (isEmpty) {
      return t('appointmentPage.signNoteOnEluve.chartNotModified');
    }
    if (!appointmentLocationId) {
      return t('appointmentPage.signNoteOnEluve.selectLocation');
    }
    if (!patientId) {
      return t('appointmentPage.signNoteOnEluve.selectPatient');
    }
  })();

  const signNoteDisabled = Boolean(signNoteDisabledReasonText);

  return (
    <div className="w-full">
      <Tooltip>
        <TooltipTrigger asChild>
          <Button disabled={signNoteDisabled} onClick={onButtonClick} fullWidth>
            {t('appointmentPage.signNote')}
          </Button>
        </TooltipTrigger>
        {signNoteDisabled && (
          <TooltipContent>{signNoteDisabledReasonText}</TooltipContent>
        )}
      </Tooltip>

      <Dialog
        open={confirmationDialogOpen}
        onOpenChange={setConfirmationDialogOpen}
      >
        <DialogContent
          size="lg"
          className="flex flex-col items-center justify-center gap-5 p-0"
        >
          <Stack className="p-10" align="center" gap={5}>
            <Heading size="3xl">{t('appointmentPage.areYouSure')}</Heading>

            <Text color="tertiary" className="text-center">
              {t('appointmentPage.noLongerEditable')}
            </Text>
            <Stack
              direction="horizontal"
              align="center"
              justify="center"
              gap={3}
            >
              <Button
                className="border border-border-secondary bg-white text-text-tertiary hover:bg-white"
                onClick={() => setConfirmationDialogOpen(false)}
              >
                {t('common.cancel')}
              </Button>

              <Button onClick={() => signNoteOnEluveOnly()}>
                {t('appointmentPage.signNoteOnEluve.confirmSignNote')}
              </Button>
            </Stack>
          </Stack>
        </DialogContent>
      </Dialog>
    </div>
  );
};
